/**
 * hubOrigin + openHubBillingTab — how any suite surface finds the hub's UI
 * origin and opens the account billing tab on it (Plan 02 D3's "Manage plan…"
 * row and every cross-link CTA).
 *
 * Resolution mirrors the sign-in gate's own seam (each app's AuthGate):
 *
 *   1. the `?host=` pane hand-off — only trusted when this page itself is on
 *      a loopback host (a public https page must never fetch a loopback URL);
 *   2. a loopback page IS the dev topology — the hub dev origin is :4040;
 *   3. otherwise the origin already serving this document's `suite-theme.js`
 *      — the hub API origin baked into every app's index.html at deploy,
 *      which serves the hub SPA (and therefore `/?view=settings&tab=billing`)
 *      alongside the chrome assets;
 *   4. same-origin as the last resort (an app whose deployment already
 *      proxies the hub plane).
 *
 * The window-opening strategies are the shared settings-window contract
 * (`suite/settings`' `openSettingsWindow`): an embedded pane hands the
 * request up via the `suite:open-settings` postMessage; a Tauri top-level
 * document creates/focuses a `WebviewWindow`; plain web opens a popup. The
 * contract constants are restated here (not imported) so the kit stays
 * self-contained for applications that alias no other suite package.
 */

/** postMessage contract between an embedded pane and its hosting shell. */
const OPEN_SETTINGS_MESSAGE = "suite:open-settings";

/** The window label for the hub's settings window (the settings kit's own
 *  `settings-<app>` convention, keyed on the hub register id). */
const HUB_SETTINGS_LABEL = "settings-ortam";

/** The hub settings deep link for the billing tab (D2's `&tab=` contract). */
const HUB_BILLING_PATH = "/?view=settings&tab=billing";

/** True for hostnames the browser treats as this machine's own loopback. */
function isLoopbackHost(hostname: string): boolean {
  return /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i.test(hostname);
}

/** The hub UI origin for THIS document, or null when it cannot be derived. */
export function hubOrigin(): string | null {
  if (typeof window === "undefined") return null;
  let pageLoopback = false;
  try {
    pageLoopback = isLoopbackHost(window.location.hostname);
  } catch {
    return null;
  }
  const fromPane = new URLSearchParams(window.location.search).get("host");
  if (fromPane) {
    try {
      const pane = new URL(fromPane, window.location.href);
      if (pageLoopback || !isLoopbackHost(pane.hostname)) return pane.origin;
    } catch {
      /* not a URL — ignore the hand-off */
    }
  }
  if (pageLoopback) return `${window.location.protocol}//${window.location.hostname}:4040`;
  const themeTag = document.querySelector<HTMLScriptElement>('script[src$="/suite-theme.js"]');
  if (themeTag) {
    try {
      return new URL(themeTag.src, window.location.href).origin;
    } catch {
      /* malformed tag src — fall through */
    }
  }
  try {
    return window.location.origin;
  } catch {
    return null;
  }
}

/** The hub's billing-tab deep link, or null when the hub origin is unknown. */
export function hubBillingUrl(): string | null {
  const origin = hubOrigin();
  return origin ? `${origin}${HUB_BILLING_PATH}` : null;
}

/** True when this document renders inside a hosting shell (the hub's pane
 *  iframe) rather than at the top level of its own window. */
function isEmbeddedPane(): boolean {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

/** The `withGlobalTauri` bundle, when this document runs under Tauri. */
interface TauriWindowHandle {
  setFocus: () => Promise<void>;
}
interface TauriGlobal {
  webviewWindow?: {
    WebviewWindow?: {
      new (label: string, options: Record<string, unknown>): {
        once: (event: string, handler: () => void) => unknown;
      };
      getByLabel?: (label: string) => Promise<TauriWindowHandle | null>;
    };
  };
}
function tauriGlobal(): TauriGlobal | null {
  if (typeof window === "undefined") return null;
  const tauri = (window as unknown as Record<string, unknown>).__TAURI__;
  return tauri && typeof tauri === "object" ? (tauri as TauriGlobal) : null;
}

/** Create (or focus) the Tauri window for one URL; false when no usable
 *  bundle exists so the caller can fall back to a popup. */
async function tauriOpenWindow(url: string, label: string): Promise<boolean> {
  const WebviewWindow = tauriGlobal()?.webviewWindow?.WebviewWindow;
  if (!WebviewWindow) return false;
  try {
    const existing = await WebviewWindow.getByLabel?.(label);
    if (existing) {
      await existing.setFocus();
      return true;
    }
  } catch {
    // Listing unavailable — try to create below.
  }
  try {
    const win = new WebviewWindow(label, { url });
    const created = await new Promise<boolean>((resolve) => {
      let settled = false;
      const done = (opened: boolean) => {
        if (!settled) {
          settled = true;
          resolve(opened);
        }
      };
      void win.once("tauri://created", () => done(true));
      void win.once("tauri://error", () => done(false));
    });
    if (created) return true;
    const raced = await WebviewWindow.getByLabel?.(label);
    return Boolean(raced);
  } catch {
    return false;
  }
}

/**
 * Open the hub's account billing tab (`/?view=settings&tab=billing`) — the
 * one action behind every "Manage plan…" row and cross-link CTA. Pane
 * documents hand the request to their hosting shell; Tauri documents create
 * (or focus) a webview window; plain web opens a popup.
 */
export function openHubBillingTab(): void {
  const url = hubBillingUrl();
  if (!url || typeof window === "undefined") return;
  if (isEmbeddedPane()) {
    window.parent.postMessage({ type: OPEN_SETTINGS_MESSAGE, app: "ortam", url, label: HUB_SETTINGS_LABEL }, "*");
    return;
  }
  void tauriOpenWindow(url, HUB_SETTINGS_LABEL).then((opened) => {
    if (!opened) window.open(url, HUB_SETTINGS_LABEL);
  });
}
